import {useState, Dispatch, SetStateAction} from 'react'
import cn from 'clsx'
import {subMonths} from 'date-fns'
import {Line} from '@ant-design/plots'
import {Space, Button, Divider} from 'antd'

import styles from './ForecastPerformance.module.scss'
import useStore from 'base/store'
import {User} from 'modules/users'
import {useMount} from 'common/hooks'
import {
  Box,
  Text,
  IconStat,
  Paper,
  Tooltip,
  Loader,
  UserAvatar,
  TargetGoalIcon,
  BlackboardChartThirdIcon,
} from 'common/components'
import {CashflowPanel} from '.'
import {
  fixNumber,
  arrayFromMonthsCount,
  formatDate,
  totalNumberFromArray,
  formatMoney,
  createReport,
} from 'common/helpers'
import {COLOR, CHART_THEME} from 'common/constants'
import {useGetForecastPerformance, ForecastPerfomanceValue} from '..'

interface ContentProps {
  forecastPerformance: any
}

interface ChartProps {
  forecastPerformance: any
  activeManagerUuid?: string
}

interface ManagerButtonProps {
  manager: User
  activeManagerUuid: string
  setActiveManagerUuid: Dispatch<SetStateAction<string>>
}

interface TooltipContentProps {
  value: ForecastPerfomanceValue
}

interface ChartData {
  months: any[]
  data: ForecastPerfomanceValue[]
}

const tooltipChartId = 'perfomanceChart'
const chartMonthsCount = 12

const getChartData = (forecastPerformance: any): ChartData => {
  const months = arrayFromMonthsCount(
    chartMonthsCount,
    subMonths(new Date(), chartMonthsCount - 1)
  )
  const data: ForecastPerfomanceValue[][] = []
  Object.entries(forecastPerformance.ratings).forEach(
    ([managerUuid, values]) => {
      const initialItems = months.map((month) => ({
        managerUuid,
        monthDate: month,
        month: formatDate(month, 'serverSecond'),
        avgVariance: null,
        items: null,
        points: null,
      }))
      const valuesEntries = Object.entries(values)
      valuesEntries.forEach(([month, value]: any) => {
        if (initialItems.some((initialItem) => initialItem.month === month)) {
          const current = initialItems.find(
            (initialItem) => initialItem.month === month
          )
          current.avgVariance = value.avgVariance
          current.items = value.items
          current.points = value.points
        }
      })
      data.push(initialItems)
    }
  )
  return {
    months: months.map((month) => ({
      month: formatDate(month, 'serverSecond'),
      label: formatDate(month, 'fourth'),
    })),
    data: data.flat(),
  }
}

const getReportData = (data: any[], managers: any[]) =>
  data.map((item) => ({
    Manager: managers.find((manager) => manager.uuid === item.managerUuid)
      ?.firstName,
    Month: formatDate(item.monthDate),
    'Average variance': fixNumber(item.avgVariance),
    Points: item.points,
  }))

const options = {
  theme: CHART_THEME,
  smooth: true,
  tooltip: false,
  limitInPlot: false,
  legend: false,
  appendPadding: [8, 0, 8, 0],
  reflect: 'y',
  xField: 'month',
  yField: 'avgVariance',
  seriesField: 'managerUuid',
  xAxis: false,
  yAxis: {
    position: 'right',
    min: 0.01,
    max: 49.99,
    label: {
      formatter: (text: string) => `${text}%`,
      style: {
        fill: COLOR.grey,
        fontWeight: 'bold',
        fontSize: 11,
        textSpacing: 1,
      },
    },
    grid: {
      line: {
        style: {
          stroke: COLOR.grey,
          lineDash: [3, 6],
        },
      },
    },
  },
}

const TooltipContent = ({value}: TooltipContentProps) => {
  if (!value) return null

  return (
    <>
      <div className={styles.tooltipBlock}>
        <Text weight="bold" mb={24}>
          {formatDate(value.monthDate, 'eight')}
        </Text>
        <ul className={styles.tooltipList}>
          {value.items.map((item, index) => (
            <li key={index} className={styles.tooltipItem}>
              <Box color="turquoizeDeep" weight="medium">
                <Space size={6}>
                  <BlackboardChartThirdIcon width={16} height={16} />
                  <span>{formatMoney(item.forecastedAmount)}</span>
                </Space>
              </Box>
              <Text weight="medium" align="right">
                {formatMoney(item.processedAmount)}
              </Text>
              <Text weight="bold" align="right">
                {item.variance}%
              </Text>
            </li>
          ))}
        </ul>
      </div>
      <Divider />
      <div className={styles.tooltipBlock}>
        <Box mb={24}>
          <Text weight="bold">{fixNumber(value.avgVariance)}%</Text>
          <Text weight="medium" color="grey">
            Average variance
          </Text>
        </Box>
        <IconStat
          row
          align="right"
          icon={<TargetGoalIcon />}
          value={`${value.points} points`}
          label={`Total ${formatDate(value.monthDate, 'fourth')} KPI`}
        />
      </div>
    </>
  )
}

const Chart = ({forecastPerformance, activeManagerUuid}: ChartProps) => {
  const isMounted = useMount()

  const chartData = getChartData(forecastPerformance)

  const config: any = {
    ...options,
    data: chartData.data,
    animation: !isMounted,
    lineStyle: ({managerUuid}: any) =>
      !activeManagerUuid || managerUuid === activeManagerUuid
        ? {
            stroke: COLOR.coal,
            lineWidth: 4,
          }
        : {
            stroke: COLOR.coal,
            opacity: 0.2,
            lineWidth: 2.5,
          },
    point: {
      size: 5.5,
      style: ({managerUuid}: any) =>
        !activeManagerUuid || managerUuid === activeManagerUuid
          ? {
              fill: COLOR.white,
              stroke: COLOR.coal,
              lineWidth: 3,
            }
          : {
              fill: 'transparent',
              opacity: 0,
            },
    },
  }

  const managerData = activeManagerUuid
    ? chartData.data.filter((value) => value.managerUuid === activeManagerUuid)
    : chartData.data

  return (
    <>
      <div className={styles.chart}>
        <div className={styles.columns}>
          {chartData.months.map((month) => (
            <div key={month.month} className={styles.column}>
              <Text
                spacing
                uppercase
                className={styles.month}
                color="turquoizeDeep"
                size="2xs"
                weight="bold"
                align="center"
              >
                {month.label}
              </Text>
            </div>
          ))}
        </div>
        <Line {...config} />
        <div className={styles.columns}>
          {chartData.months.map((month) => (
            <div
              key={month.month}
              className={cn(styles.column, styles.column_tooltip)}
              data-for={tooltipChartId}
              data-tip={month.month}
            />
          ))}
        </div>
      </div>
      <Tooltip
        id={tooltipChartId}
        getContent={(month) => {
          const value = managerData.find((value) => value.month === month)
          return value?.avgVariance !== null ? (
            <TooltipContent value={value} />
          ) : null
        }}
      />
    </>
  )
}

const ManagerButton = ({
  manager,
  activeManagerUuid,
  setActiveManagerUuid,
}: ManagerButtonProps) => {
  const handleClick = () => setActiveManagerUuid(manager.uuid)

  return (
    <Button
      className={cn(
        styles.manager,
        activeManagerUuid === manager.uuid && styles.manager_active
      )}
      onClick={handleClick}
    >
      <Space>
        <UserAvatar src={manager.photo} size={36} alt={manager.firstName} />
        <Text weight="medium">{manager.firstName}</Text>
      </Space>
      <Box align="right">
        <Text size="2xl">{manager.manager?.rating ?? 0}</Text>
        <Text size="xs" color="grey" weight="medium">
          points
        </Text>
      </Box>
    </Button>
  )
}

const GeneralContent = ({forecastPerformance}: ContentProps) => {
  const [activeManagerUuid, setActiveManagerUuid] = useState<string>(
    forecastPerformance.managers[0]?.uuid
  )

  return (
    <div className={styles.wrapper}>
      <Chart
        forecastPerformance={forecastPerformance}
        activeManagerUuid={activeManagerUuid}
      />
      <ul className={styles.managers}>
        {forecastPerformance.managers.map((manager: User) => (
          <li key={manager.uuid}>
            <ManagerButton
              manager={manager}
              activeManagerUuid={activeManagerUuid}
              setActiveManagerUuid={setActiveManagerUuid}
            />
          </li>
        ))}
      </ul>
    </div>
  )
}

const ManagerContent = ({forecastPerformance}: ContentProps) => {
  const ratingsValues = Object.values(forecastPerformance.ratings)
  const ratings = ratingsValues.length ? Object.values(ratingsValues[0]) : []
  const avrPoints = fixNumber(
    totalNumberFromArray(ratings, 'points') / ratings.length || 0
  )
  const avrVariance = fixNumber(
    totalNumberFromArray(ratings, 'avgVariance') / ratings.length || 0
  )

  return (
    <div className={cn(styles.wrapper, styles.wrapper_manager)}>
      <div className={styles.block}>
        <Text weight="medium">
          Average KPI
          <br />
          for last {chartMonthsCount} months
        </Text>
        <IconStat
          icon={<TargetGoalIcon />}
          value={`${avrPoints} points`}
          label={`${avrVariance}% variance`}
        />
      </div>
      <Chart forecastPerformance={forecastPerformance} />
    </div>
  )
}

export default function ForecastPerformance() {
  const role = useStore((state) => state.role)
  const {data: forecastPerformance} = useGetForecastPerformance()

  const handleReport = () => {
    const data = getChartData(forecastPerformance)
    createReport(
      getReportData(data.data, forecastPerformance.managers),
      'Forecast performance',
      'forecast-performance-report'
    )
  }

  return (
    <Paper>
      <CashflowPanel
        title="Forecast performance"
        reportDisabled={
          !forecastPerformance ||
          (forecastPerformance && !forecastPerformance.ratings.length)
        }
        onReport={handleReport}
      />
      {forecastPerformance ? (
        role.manager ? (
          <ManagerContent forecastPerformance={forecastPerformance} />
        ) : (
          <GeneralContent forecastPerformance={forecastPerformance} />
        )
      ) : (
        <Loader className={styles.loader} />
      )}
    </Paper>
  )
}
